import React, { useState, useEffect } from 'react';
import { CreditCard, Lock, Phone, MapPin } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentForm = ({ paymentData, setPaymentData, onSubmit, isProcessing, errors, bookingData }) => {
  const [activeSection, setActiveSection] = useState('payment-method');
  const [formErrors, setFormErrors] = useState({});

  const paymentMethods = [
    { value: 'credit-card', label: 'Credit/Debit Card', icon: CreditCard },
    { value: 'paypal', label: 'PayPal', icon: null },
    { value: 'apple-pay', label: 'Apple Pay', icon: null },
    { value: 'google-pay', label: 'Google Pay', icon: null },
    { value: 'buy-now-pay-later', label: 'Buy Now, Pay Later', icon: null }
  ];

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'ES', label: 'Spain' },
    { value: 'IT', label: 'Italy' },
    { value: 'AU', label: 'Australia' },
    { value: 'JP', label: 'Japan' }
  ];

  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setPaymentData(prev => ({
        ...prev,
        [section]: {
          ...prev?.[section],
          [field]: value
        }
      }));
    } else {
      setPaymentData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear field error when user starts typing
    if (formErrors?.[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Auto-format card number
  const formatCardNumber = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    const matches = v?.match(/\d{4,16}/g);
    const match = matches && matches?.[0] || '';
    const parts = [];
    for (let i = 0, len = match?.length; i < len; i += 4) {
      parts?.push(match?.substring(i, i + 4));
    }
    if (parts?.length) {
      return parts?.join(' ');
    } else {
      return v;
    }
  };

  // Auto-format expiry date
  const formatExpiryDate = (value) => {
    const v = value?.replace(/\s+/g, '')?.replace(/[^0-9]/gi, '');
    if (v?.length >= 2) {
      return v?.substring(0, 2) + (v?.length > 2 ? '/' + v?.substring(2, 4) : '');
    }
    return v;
  };

  const validateForm = () => {
    const errors = {};

    // Payment method validation
    if (!paymentData?.paymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }

    // Credit card validation
    if (paymentData?.paymentMethod === 'credit-card') {
      if (!paymentData?.cardInfo?.cardNumber) {
        errors['cardInfo.cardNumber'] = 'Card number is required';
      } else if (paymentData?.cardInfo?.cardNumber?.replace(/\s/g, '')?.length < 16) {
        errors['cardInfo.cardNumber'] = 'Invalid card number';
      }

      if (!paymentData?.cardInfo?.expiryDate) {
        errors['cardInfo.expiryDate'] = 'Expiry date is required';
      } else if (!paymentData?.cardInfo?.expiryDate?.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) {
        errors['cardInfo.expiryDate'] = 'Invalid expiry date (MM/YY)';
      }

      if (!paymentData?.cardInfo?.cvv) {
        errors['cardInfo.cvv'] = 'CVV is required';
      } else if (paymentData?.cardInfo?.cvv?.length < 3) {
        errors['cardInfo.cvv'] = 'Invalid CVV';
      }

      if (!paymentData?.cardInfo?.cardholderName) {
        errors['cardInfo.cardholderName'] = 'Cardholder name is required';
      }
    }

    // Billing address validation
    if (!paymentData?.billingAddress?.fullName) {
      errors['billingAddress.fullName'] = 'Full name is required';
    }

    if (!paymentData?.billingAddress?.email) {
      errors['billingAddress.email'] = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(paymentData?.billingAddress?.email)) {
      errors['billingAddress.email'] = 'Invalid email address';
    }

    if (!paymentData?.billingAddress?.phone) {
      errors['billingAddress.phone'] = 'Phone number is required';
    }

    if (!paymentData?.billingAddress?.address) {
      errors['billingAddress.address'] = 'Address is required';
    }

    if (!paymentData?.billingAddress?.city) {
      errors['billingAddress.city'] = 'City is required';
    }

    if (!paymentData?.billingAddress?.zipCode) {
      errors['billingAddress.zipCode'] = 'ZIP code is required';
    }

    if (!paymentData?.billingAddress?.country) {
      errors['billingAddress.country'] = 'Country is required';
    }

    // Terms acceptance
    if (!paymentData?.acceptTerms) {
      errors.acceptTerms = 'Please accept the terms and conditions';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(paymentData);
    }
  };

  const getFieldError = (field) => {
    return formErrors?.[field] || errors?.[field];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Payment Method Selection */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
          <Lock className="w-5 h-5 text-green-600" />
          <span>Payment Method</span>
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {paymentMethods?.map((method) => (
            <div key={method?.value} className="relative">
              <input
                type="radio"
                id={method?.value}
                name="paymentMethod"
                value={method?.value}
                checked={paymentData?.paymentMethod === method?.value}
                onChange={(e) => handleInputChange('paymentMethod', e?.target?.value)}
                className="sr-only"
              />
              <label
                htmlFor={method?.value}
                className={`block p-4 border rounded-lg cursor-pointer transition-all ${
                  paymentData?.paymentMethod === method?.value
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {method?.icon && <method.icon className="w-5 h-5 text-gray-600" />}
                  <span className="font-medium text-gray-900">{method?.label}</span>
                </div>
              </label>
            </div>
          ))}
        </div>
        {getFieldError('paymentMethod') && (
          <p className="text-sm text-red-600">{getFieldError('paymentMethod')}</p>
        )}
      </div>
      {/* Credit Card Details */}
      {paymentData?.paymentMethod === 'credit-card' && (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900">Card Information</h3>
          
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Card Number"
              type="text"
              value={paymentData?.cardInfo?.cardNumber}
              onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e?.target?.value), 'cardInfo')}
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              error={getFieldError('cardInfo.cardNumber')}
              required
            />
            
            <Input
              label="Cardholder Name"
              type="text"
              value={paymentData?.cardInfo?.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e?.target?.value, 'cardInfo')}
              placeholder="John Doe"
              error={getFieldError('cardInfo.cardholderName')}
              required
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                value={paymentData?.cardInfo?.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e?.target?.value), 'cardInfo')}
                placeholder="MM/YY"
                maxLength={5}
                error={getFieldError('cardInfo.expiryDate')}
                required
              />
              
              <Input
                label="CVV"
                type="text"
                value={paymentData?.cardInfo?.cvv}
                onChange={(e) => handleInputChange('cvv', e?.target?.value?.replace(/\D/g, ''), 'cardInfo')}
                placeholder="123"
                maxLength={4}
                error={getFieldError('cardInfo.cvv')}
                required
              />
            </div>
          </div>
        </div>
      )}
      {/* Billing Address */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          <span>Billing Address</span>
        </h2>
        
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Full Name"
            type="text"
            value={paymentData?.billingAddress?.fullName}
            onChange={(e) => handleInputChange('fullName', e?.target?.value, 'billingAddress')}
            error={getFieldError('billingAddress.fullName')}
            required
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={paymentData?.billingAddress?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value, 'billingAddress')}
              error={getFieldError('billingAddress.email')}
              required
            />
            
            <Input
              label="Phone"
              type="tel"
              value={paymentData?.billingAddress?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value, 'billingAddress')}
              error={getFieldError('billingAddress.phone')}
              required
            />
          </div>
          
          <Input
            label="Address"
            type="text"
            value={paymentData?.billingAddress?.address}
            onChange={(e) => handleInputChange('address', e?.target?.value, 'billingAddress')}
            error={getFieldError('billingAddress.address')}
            required
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="City"
              type="text"
              value={paymentData?.billingAddress?.city}
              onChange={(e) => handleInputChange('city', e?.target?.value, 'billingAddress')}
              error={getFieldError('billingAddress.city')}
              required
            />
            
            <Input
              label="State/Province"
              type="text"
              value={paymentData?.billingAddress?.state}
              onChange={(e) => handleInputChange('state', e?.target?.value, 'billingAddress')}
              error={getFieldError('billingAddress.state')}
            />
            
            <Input
              label="ZIP/Postal Code"
              type="text"
              value={paymentData?.billingAddress?.zipCode}
              onChange={(e) => handleInputChange('zipCode', e?.target?.value, 'billingAddress')}
              error={getFieldError('billingAddress.zipCode')}
              required
            />
          </div>
          
          <Select
            label="Country"
            options={countries}
            value={paymentData?.billingAddress?.country}
            onChange={(value) => handleInputChange('country', value, 'billingAddress')}
            placeholder="Select country"
            error={getFieldError('billingAddress.country')}
            required
          />
        </div>
      </div>
      {/* Account & Preferences */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Account Options</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="guestCheckout"
              checked={paymentData?.guestCheckout}
              onChange={(e) => handleInputChange('guestCheckout', e?.target?.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="guestCheckout" className="text-sm text-gray-700">
              Continue as guest (recommended for quick checkout)
            </label>
          </div>
          
          {!paymentData?.guestCheckout && (
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="createAccount"
                checked={paymentData?.createAccount}
                onChange={(e) => handleInputChange('createAccount', e?.target?.checked)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="createAccount" className="text-sm text-gray-700">
                Create an account for faster future bookings and exclusive offers
              </label>
            </div>
          )}
          
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="savePaymentInfo"
              checked={paymentData?.savePaymentInfo}
              onChange={(e) => handleInputChange('savePaymentInfo', e?.target?.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="savePaymentInfo" className="text-sm text-gray-700">
              Securely save payment information for future bookings
            </label>
          </div>
          
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="subscribeNewsletter"
              checked={paymentData?.subscribeNewsletter}
              onChange={(e) => handleInputChange('subscribeNewsletter', e?.target?.checked)}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="subscribeNewsletter" className="text-sm text-gray-700">
              Subscribe to our newsletter for travel tips and exclusive deals
            </label>
          </div>
        </div>
      </div>
      {/* Terms and Conditions */}
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="acceptTerms"
            checked={paymentData?.acceptTerms}
            onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            required
          />
          <label htmlFor="acceptTerms" className="text-sm text-gray-700">
            I agree to the{' '}
            <a href="/terms" className="text-blue-600 hover:text-blue-700 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-blue-600 hover:text-blue-700 underline">
              Privacy Policy
            </a>
            {' '}*
          </label>
        </div>
        {getFieldError('acceptTerms') && (
          <p className="text-sm text-red-600">{getFieldError('acceptTerms')}</p>
        )}
      </div>
      {/* Submit Button */}
      <div className="pt-6 border-t border-gray-200">
        <Button
          type="submit"
          loading={isProcessing}
          disabled={isProcessing}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg font-semibold"
        >
          {isProcessing ? 'Processing Payment...' : `Complete Payment - $${bookingData?.totalAmount}`}
        </Button>
        
        {errors?.submit && (
          <p className="mt-2 text-sm text-red-600 text-center">{errors?.submit}</p>
        )}
        
        <p className="mt-4 text-xs text-gray-500 text-center">
          Your payment is secured with 256-bit SSL encryption. No hidden fees.
        </p>
      </div>
    </form>
  );
};

export default PaymentForm;